import { useState } from 'react';
import { Box, IconButton, Button, Grid2, Typography } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import EditIcon from '@mui/icons-material/Edit';
import CustomDivider from '../../shared/utils/CustomDivider';
import TableFilterTemplate from '../../shared/utils/TableFilterTemplate';
import { NormalContainerWithHeader } from '../../shared/components/student/NormalComponent';
import ToggleSwitch from '../../shared/utils/CustomToggle';
import { Grid_Gap, Input_Width } from '../../shared/constants';
import { TextFormField } from '../../shared/components/formfields/TextFormField';
import { useGetBoards, useCreateBoard, useUpdateBoard, useUpdateBoardStatus } from '../../assets/api/useMastersApi';

const BoardMaster = () => {
    const [selectedItem, setSelectedItem] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    
    const { data: boardsData, isLoading } = useGetBoards({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize
    });
    
    const boardsList = Array.isArray(boardsData?.data) ? boardsData.data : (Array.isArray(boardsData) ? boardsData : []);

    // Add sr_no dynamically if not present
    const formattedRows = boardsList.map((row, index) => ({
        ...row,
        sr_no: row.sr_no || (index + 1) + (paginationModel.page * paginationModel.pageSize)
    }));

    const totalRows = boardsData?.pagination?.totalElements 
        || boardsData?.pagination?.total 
        || boardsData?.total 
        || boardsData?.data?.total 
        || boardsData?.totalRecords 
        || boardsData?.count
        || boardsData?.data?.count
        || boardsList.length;

    const validationSchema = Yup.object().shape({
        name: Yup.string().required('Board name is required'),
    });

    const initialValues = {
        name: selectedItem?.name || '',
    };

    const createMutation = useCreateBoard(() => {
        setIsEditing(false);
        setSelectedItem(null);
    });

    const updateMutation = useUpdateBoard(() => {
        setIsEditing(false);
        setSelectedItem(null);
    });

    const statusMutation = useUpdateBoardStatus();

    const handleSubmit = (values, { resetForm }) => {
        if (isEditing) {
            updateMutation.mutate({ id: selectedItem.id, payload: values });
        } else {
            createMutation.mutate(values);
            resetForm();
        }
    };

    const columns = [
        { field: 'sr_no', headerName: 'Sr No', flex: 0.5, renderCell: (params) => <Typography>{params.value || (params.api.getAllRowIds().indexOf(params.id) + 1) + (paginationModel.page * paginationModel.pageSize)}</Typography> },
        { field: 'name', headerName: 'Board Name', flex: 2 },
        {
            field: 'actionsEdit',
            headerName: 'Edit',
            flex: 1,
            renderCell: (params) => (
                <IconButton
                    onClick={() => {
                        setSelectedItem(params.row);
                        setIsEditing(true);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    size="small"
                >
                    <EditIcon />
                </IconButton>
            ),
        },
        {
            field: 'actionsToggle',
            headerName: 'Status',
            flex: 1,
            renderCell: (params) => (
                <ToggleSwitch 
                    checked={params.row.status === true || params.row.status === 1} 
                    onCheck={() => {
                        const newStatus = !(params.row.status === true || params.row.status === 1);
                        statusMutation.mutate({ id: params.row.id, payload: { status: newStatus } });
                    }} 
                />
            ),
        },
    ];

    return (
        <Box>
            <CustomDivider />
            <NormalContainerWithHeader headerText={isEditing ? 'Edit Board' : 'Add Board'}>
                <Formik 
                    initialValues={initialValues} 
                    validationSchema={validationSchema} 
                    onSubmit={handleSubmit} 
                    enableReinitialize
                >
                    {({ resetForm }) => (
                        <Form>
                            <Grid2 container spacing={Grid_Gap}>
                                <Grid2 size={Input_Width}>
                                    <Field name="name" component={TextFormField} label="Board Name" isRequired />
                                </Grid2>
                            </Grid2>
                            <CustomDivider />
                            <Grid2 container spacing={Grid_Gap} sx={{ justifyContent: "end" }}>
                                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            resetForm();
                                            setIsEditing(false);
                                            setSelectedItem(null);
                                        }}
                                        fullWidth
                                    >
                                        {isEditing ? 'Cancel' : 'Reset'}
                                    </Button>
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        fullWidth
                                        disabled={createMutation.isPending || updateMutation.isPending}
                                    >
                                        {isEditing ? 'Update' : 'Submit'}
                                    </Button>
                                </Grid2>
                            </Grid2>
                        </Form>
                    )}
                </Formik>
            </NormalContainerWithHeader>

            <CustomDivider />

            <TableFilterTemplate
                title="Board List"
                columns={columns}
                rowsData={formattedRows}
                isLoading={isLoading} 
                isServerSide={true}
                totalServerRows={totalRows}
                onServerPaginationChange={setPaginationModel}
            />
        </Box>
    );
};

export default BoardMaster;
